import React, { useMemo } from 'react';
import CustomLineChart from 'shared/charts/LineChart';
import CustomPieChart from 'shared/charts/PieChart';
import { Card, CardHeader, CardTitle, CardContent } from 'shared/ui/Card';
import { Tabs, Tab } from 'shared/ui/Tabs';

const YearlyStatsChart = ({ data }) => {
  // Extract years from the data
  const years = useMemo(() => 
    data.year_stats?.map(yearData => ({
      id: yearData.year,
      name: yearData.year.toString()
    })) || []
  , [data]);

  const formatMonthlyData = (metricKey) => {
    const formattedData = {};
    
    // Initialize with all months
    const months = [
      { month: 1, name: 'January' },
      { month: 2, name: 'February' },
      { month: 3, name: 'March' },
      { month: 4, name: 'April' },
      { month: 5, name: 'May' },
      { month: 6, name: 'June' },
      { month: 7, name: 'July' },
      { month: 8, name: 'August' },
      { month: 9, name: 'September' },
      { month: 10, name: 'October' },
      { month: 11, name: 'November' },
      { month: 12, name: 'December' }
    ];

    months.forEach(month => {
      formattedData[month.month] = { month: month.name };
    });

    // Fill in year values for each month
    data.year_stats?.forEach(yearData => {
      const yearId = yearData.year;
      yearData.monthly_stats.forEach(monthly => {
        if (formattedData[monthly.month]) {
          formattedData[monthly.month][yearId] = monthly[metricKey];
        }
      });
    });

    return Object.values(formattedData);
  };

  const calculateTotalsByYear = (metricKey) => {
    return data.year_stats?.map(yearData => ({
      name: yearData.year.toString(),
      value: yearData.total_stats[`total_${metricKey}`] || 0
    })) || [];
  };

  const chartConfigs = {
    orders: {
      title: 'Количество продаж',
      subtitle: 'Количество продаж по годам',
      metric: 'month_count',
      totalMetric: 'count'
    },
    revenue: {
      title: 'Выручка',
      subtitle: 'Общая сумма продаж по годам (сом)',
      metric: 'month_sum',
      totalMetric: 'sum'
    },
    profit: {
      title: 'Прибыль',
      subtitle: 'Чистая прибыль по годам (сом)',
      metric: 'month_profit',
      totalMetric: 'profit'
    },
    costs: {
      title: 'Себестоимость',
      subtitle: 'Себестоимость по годам (сом)',
      metric: 'month_purchase',
      totalMetric: 'purchase'
    },
    discounts: {
      title: 'Скидки',
      subtitle: 'Общая сумма предоставленных скидок по годам (сом)',
      metric: 'month_discount_sum',
      totalMetric: 'discount_sum'
    },
    bonuses: {
      title: 'Бонусный кэшбэк',
      subtitle: 'Общая сумма начисленного бонусного кэшбэка (сом)',
      metric: 'month_bonus_cashback',
      totalMetric: 'bonus_cashback'
    },
    bonusSpent: {
      title: 'Потраченные бонусы',
      subtitle: 'Общая сумма потраченных бонусов (сом)',
      metric: 'month_bonus_spent',
      totalMetric: 'bonus_spent'
    }
  };

  const chartsData = useMemo(() => {
    const formattedData = {};
    Object.entries(chartConfigs).forEach(([key, config]) => {
      formattedData[key] = {
        barData: formatMonthlyData(config.metric),
        pieData: calculateTotalsByYear(config.totalMetric)
      };
    });
    return formattedData;
  }, [data]);

  const linechartConfig = {
    dataKeys: years.map(year => year.id),
    labels: years.map(year => year.name),
    colors: ['#8884d8', '#82ca9d', '#ffc658', '#a4de6c', '#ff8042', 
             '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#a4de6c', '#ff8042']
  };

  const renderMetricTab = (key, config) => {
    return (
      <div className='py-4'>
        <div className='grid grid-cols-1 gap-6'>
          <Card className='w-full'>
            <CardHeader>
              <CardTitle>{config.title} - Ежемесячная тенденция</CardTitle>
              <p className='text-sm text-gray-600'>{config.subtitle}</p>
            </CardHeader>
            <CardContent>
              <div className='h-96'>
                <CustomLineChart 
                  data={chartsData[key].barData}
                  config={linechartConfig}
                />
              </div>
            </CardContent>
          </Card>
          
          <Card className='w-full'>
            <CardHeader>
              <CardTitle>{config.title} - Распределение по годам</CardTitle>
              <p className='text-sm text-gray-600'>Общее распределение по годам</p>
            </CardHeader>
            <CardContent>
              <div className='h-96'>
                <CustomPieChart 
                  data={chartsData[key].pieData}
                  config={{colors: linechartConfig.colors}}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  // Show loading or empty state if no data
  if (!data.year_stats || data.year_stats.length === 0) {
    return (
      <div className='container mx-auto p-4'>
        <Card className='w-full'>
          <CardHeader>
            <CardTitle>Статистика по годам</CardTitle>
            <p className='text-sm text-gray-600'>Выберите годы для отображения статистики</p>
          </CardHeader>
          <CardContent>
            <div className='text-center py-8 text-gray-500'>
              Нет данных для отображения
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='container mx-auto p-4'>
      <Tabs initialTab='orders'>
        {Object.entries(chartConfigs).map(([key, config]) => (
          <Tab key={key} label={config.title}>
            {renderMetricTab(key, config)}
          </Tab>
        ))}
      </Tabs>
    </div>
  );
};

export default YearlyStatsChart; 